'use client';
/*
 * @providesModule CheckoutController
 *
 */
import getGameId from '@/utils/getGameId';
import {
  CashDisabledLocationController,
  CheckoutFlowConfig,
  ClientPropertyConstants,
  CurrentUserStore,
  ErrorDialogModal,
  LoadingIndicator,
  ModalNavigationController,
  PaymentsAPIController,
  SkillzRouter,
  StoreFrontStore,
} from '@sdk-mobile';
import Cookies from 'js-cookie';
import {PromoCode} from '@/app/(app)/games/[gid]/(main)/store/_src/usePromoCode';
import {IOffersEntity} from '@/app/(app)/games/[gid]/(main)/store/interfaces';
import {INTERSTITIAL_COOKIE_NAME} from '@/utils/constant';
import {StoredCard} from '@/services/payments/types';

export const PROMO_COOKIE_NAME = 'promocode';

// Deposit/withdraw navigations push the raw `/games/${gameId}/…` path; on the shell
// game (19369) RouteHandler's remapUrl (see SDKProvider) rewrites it to the clean
// `/deposit/{id}` or `/partner/{slug}/deposit/{id}`, so no per-call remap is needed here.
const gameId = getGameId();

const COOKIE_OPTIONS: Cookies.CookieAttributes = {
  expires: 1 / 24,
  secure: true,
  sameSite: 'lax',
};

const CheckoutController = {
  launchCheckoutFlow: async (config: CheckoutFlowConfig) => {
    const {offer, promo} = config;
    try {
      const cashDisabledLocationController = new CashDisabledLocationController();
      LoadingIndicator.show();
      const result = await cashDisabledLocationController.checkForCashDisabledLocation({
        popToTop: () => {
          SkillzRouter.replace(`/games/${gameId}/home`);
        },
      });

      if (!result) {
        LoadingIndicator.hide();
        return;
      }

      if (promo) {
        Cookies.set(
          PROMO_COOKIE_NAME,
          JSON.stringify({
            amount: Number(promo.cash_amount),
            code: promo.code,
            type: promo.type,
          }),
          COOKIE_OPTIONS
        );
      } else {
        Cookies.remove(PROMO_COOKIE_NAME);
      }

      // Store complete offer data
      const offerData = {
        ...offer,
        amount: Number(offer.amount),
        bonus: Number(offer.bonus),
        id: offer.id,
        is_interstitial_purchase: Boolean(offer.is_interstitial_purchase),
      };

      Cookies.set('checkout_offer_data', JSON.stringify(offerData), COOKIE_OPTIONS);

      if (offer.is_interstitial_purchase) {
        Cookies.set(INTERSTITIAL_COOKIE_NAME, JSON.stringify(offer.id), COOKIE_OPTIONS);
      } else {
        Cookies.remove(INTERSTITIAL_COOKIE_NAME);
      }

      LoadingIndicator.hide();
      await CheckoutController._handlePendingWithdrawal(offer.id);
      config.onCheckoutDisplayed?.();
    } catch (e) {
      console.log('error on pushing to deposit page: ', e);
      // Clean up cookies in case of error
      Cookies.remove(PROMO_COOKIE_NAME);
      Cookies.remove('checkout_offer_data');
      Cookies.remove(INTERSTITIAL_COOKIE_NAME);
    }
  },
  isLaunchingCheckout: () => false,
  getPromoAmount(offer: IOffersEntity, promo?: PromoCode): number {
    let promoAmount = 0;
    if (promo) {
      if (promo.type == 'PERCENT') {
        promoAmount = (Number(promo.cash_amount) / 100.0) * Number(offer.amount);
      } else {
        promoAmount = Number(promo.cash_amount);
      }
    }
    return promoAmount;
  },
  getStoreData: async () => {
    return new Promise((resolve) => {
      StoreFrontStore.fetchDepositOffersWithCompletion(false, (storeInfo: any) => {
        resolve(storeInfo);
      });
    });
  },
  async _handlePendingWithdrawal(depositId: string) {
    try {
      LoadingIndicator.show();
      const [paymentInfo, userInfo] = await Promise.all([
        PaymentsAPIController.getPaymentsInformation(),
        CurrentUserStore.fetchUser(false),
      ]);
      const withdrawRequests = paymentInfo.open_withdrawal_requests;
      if (
        withdrawRequests.length > 0 &&
        !ClientPropertyConstants.getIsCheckoutEnabledWithPendingWithdrawal()
      ) {
        let withdrawalsAmount = 0;
        withdrawRequests.forEach((withdrawal) => {
          withdrawalsAmount += withdrawal.bonus;
          withdrawalsAmount += withdrawal.amount;
        });
        const newBalance = userInfo.cash_balance + withdrawalsAmount;
        let newUserInfo = userInfo;
        try {
          newUserInfo = JSON.parse(JSON.stringify(userInfo));
          newUserInfo.cash_balance = newBalance;
        } catch (error) {
          console.warn('error parsing user info: ' + JSON.stringify(error));
        }
        LoadingIndicator.hide();
        SkillzRouter.push(`/games/${gameId}/store/withdrawpending`, {withdrawRequests, depositId});
      } else {
        LoadingIndicator.hide();
        SkillzRouter.push(`/games/${gameId}/deposit/${depositId}`);
      }
    } catch (e) {
      LoadingIndicator.hide();
      console.warn('display checkout flow error ' + JSON.stringify(e));
    }
  },

  deleteStoredCard: async (card: StoredCard): Promise<void> => {
    LoadingIndicator.show();
    try {
      await PaymentsAPIController.deleteStoredCard(card.id);
    } catch (e) {
      LoadingIndicator.hide();

      const modalProps = {
        animationType: 'fade',
        transparent: true,
      };
      const errorDialog = (
        <ErrorDialogModal
          retryCallback={() => {
            CheckoutController.deleteStoredCard(card);
          }}
          onDone={() => {
            LoadingIndicator.hide();
            ModalNavigationController.hideModal();
          }}
          error={e}
        />
      );

      ModalNavigationController.showModal(errorDialog, modalProps);
    }
  },
};

export default CheckoutController;
